// src/features/insights/hooks/useInsightEngagement.js
// Custom hook for tracking insight engagement
// Part of Personal AI Memory System - Phase 3

import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../auth/context/AuthContext';
import {
  trackInsightEngagement,
  trackInsightDismissal
} from '../services/aiMemoryService';
import logger from '@/shared/utils/core/logger';

/**
 * Hook to track insight engagement (view time, interactions)
 * @param {string} insightId - Unique insight ID
 * @param {Object} insight - Insight data (type, content)
 * @returns {Object} - Engagement tracking functions
 */
export function useInsightEngagement(insightId, insight = {}) {
  const { user } = useAuth();
  const viewStartTime = useRef(null);
  const hasTrackedView = useRef(false);

  // Start tracking when component mounts
  useEffect(() => {
    if (insightId && !hasTrackedView.current) {
      viewStartTime.current = Date.now();
      hasTrackedView.current = true;
      logger.debug('[InsightEngagement] Started tracking view:', insightId);
    }
  }, [insightId]);

  /**
   * Track positive engagement (saved, shared, acknowledged)
   */
  const trackEngaged = useCallback(async (response = 'engaged') => {
    if (!user?.uid || !insightId) return;

    const timeSpent = viewStartTime.current
      ? Math.floor((Date.now() - viewStartTime.current) / 1000)
      : 0;

    try {
      await trackInsightEngagement(user.uid, insightId, {
        insightType: insight.type || 'general',
        content: insight.content || '',
        response, // 'engaged', 'saved', 'shared'
        timeSpent
      });

      logger.debug('[InsightEngagement] Tracked engagement:', {
        insightId,
        response,
        timeSpent
      });
    } catch (error) {
      logger.error('[InsightEngagement] Failed to track engagement:', error);
    }
  }, [user?.uid, insightId, insight]);

  /**
   * Track dismissal
   */
  const trackDismissed = useCallback(async () => {
    if (!user?.uid || !insightId) return;

    const timeSpent = viewStartTime.current
      ? Math.floor((Date.now() - viewStartTime.current) / 1000)
      : 0;

    // Determine dismissal speed
    let dismissalSpeed = 'unknown';
    if (timeSpent < 3) dismissalSpeed = 'immediate';
    else if (timeSpent < 10) dismissalSpeed = 'quick';
    else dismissalSpeed = 'after_reading';

    try {
      await trackInsightDismissal(user.uid, insightId, {
        insightType: insight.type || 'general',
        content: insight.content || '',
        dismissalSpeed
      });

      logger.debug('[InsightEngagement] Tracked dismissal:', {
        insightId,
        dismissalSpeed,
        timeSpent
      });
    } catch (error) {
      logger.error('[InsightEngagement] Failed to track dismissal:', error);
    }
  }, [user?.uid, insightId, insight]);

  /**
   * Track specific action (thanks, explore, etc.)
   */
  const trackAction = useCallback(async (action) => {
    const actionMap = {
      thanks: 'engaged',
      explore: 'engaged',
      save: 'saved',
      share: 'shared'
    };

    const response = actionMap[action] || 'engaged';
    await trackEngaged(response);
  }, [trackEngaged]);

  return {
    trackEngaged,
    trackDismissed,
    trackAction
  };
}

/**
 * Hook to track time spent viewing insight content
 * Automatically tracks on unmount
 */
export function useInsightViewTracking(insightId, insight = {}) {
  const { user } = useAuth();
  const viewStartTime = useRef(Date.now());

  useEffect(() => {
    // Track view on unmount
    return () => {
      if (!user?.uid || !insightId) return;

      const timeSpent = Math.floor((Date.now() - viewStartTime.current) / 1000);

      // Only track if user spent at least 2 seconds viewing
      if (timeSpent >= 2) {
        trackInsightEngagement(user.uid, insightId, {
          insightType: insight.type || 'general',
          content: insight.content || '',
          response: 'engaged',
          timeSpent
        }).catch(error => {
          logger.error('[InsightViewTracking] Failed to track view:', error);
        });
      }
    };
  }, [user?.uid, insightId, insight]);
}

export default useInsightEngagement;
